import { initCart } from "./cart.js";
import { logNavigation } from "./analytics.js";
import { initForms } from "./ui.js";

export function initRouter(addToCart) {
  const routerEvent = new Event("mossy-router");

  return new Promise((resolve) => {
    // Important States
    let currentRoute = null;
    let latestPath = null;
    const pathsBeingFetched = new Set();
    let observer;
    let isFirstLoad = true;

    let loading = {
      bar: null,
      progress: 0,
      interval: null,
    };

    // Core Router
    async function router() {
      const path = window.location.pathname || "/";
      const queryString = window.location.search || "";
      const fullPath = path + queryString;

      currentRoute = fullPath;
      try {
        latestPath = fullPath;
        await updateContent(fullPath);
      } catch (error) {
        console.error("Failed to fetch content:", error);
      } finally {
        if (latestPath === fullPath) {
          completeLoading();
        }
      }
      attachEventListeners();
    }

    // Loading Bar Functions
    function createLoadingBar() {
      if (!loading.bar) {
        loading.bar = document.createElement("div");
        loading.bar.id = "loading-bar";
        loading.bar.style.cssText = `
          position: fixed; top: 0; left: 0; height: 3px; width: 0; background: var(--mossy-fg-color); transition: width 0.2s ease-in-out; z-index: 9999;
        `;
        document.body.appendChild(loading.bar);
      }
    }

    function startLoading() {
      clearInterval(loading.interval);
      if (loading.bar) {
        loading.bar.remove();
      }
      loading.bar = null;
      loading.progress = 0;

      createLoadingBar();
      loading.interval = setInterval(() => {
        if (loading.progress < 95) {
          loading.progress += Math.random() * 5;
          updateLoadingBar();
        }
      }, 200);
    }

    function updateLoadingBar() {
      if (loading.bar) {
        loading.bar.style.width = loading.progress + "%";
      }
    }

    function completeLoading() {
      clearInterval(loading.interval);
      loading.progress = 100;
      updateLoadingBar();
      setTimeout(() => {
        if (loading.bar) {
          loading.bar.remove();
          loading.bar = null;
        }
        loading.progress = 0;
      }, 200);
    }

    // Cache & Content Fetching
    function isCacheExpired(headers) {
      let isExpired = false;

      const cacheControl = headers.get("Cache-Control");
      const expires = headers.get("Expires");

      if (cacheControl) {
        const maxAgeMatch = cacheControl.match(/max-age=(\d+)/);

        if (maxAgeMatch) {
          const maxAge = parseInt(maxAgeMatch[1], 10);
          const timeFetched = headers.get("Date");
          if (timeFetched) {
            const timeFetchedInSeconds = Date.parse(timeFetched) / 1000;
            const timeNowInSeconds = Date.now() / 1000;

            const age = timeNowInSeconds - timeFetchedInSeconds;

            if (age > maxAge) {
              isExpired = true;
            }
          }
        } else if (
          cacheControl.includes("no-store") ||
          cacheControl.includes("no-cache")
        ) {
          isExpired = true;
        }
      }

      if (!isExpired && expires) {
        const expiresDate = new Date(expires);
        if (new Date() > expiresDate) {
          isExpired = true;
        }
      }

      return isExpired;
    }

    async function fetchContent(fullPath, isPrefetch = false) {
      // Check if the path is already being fetched
      if (pathsBeingFetched.has(fullPath)) {
        return; // Exit if already fetching
      }

      pathsBeingFetched.add(fullPath); // Add full path to active fetch paths
      const cache = await caches.open("route-cache");

      try {
        const cachedResponse = await cache.match(fullPath);
        let headers = {};

        // If there's cached content, add validation headers
        if (cachedResponse) {
          const eTag = cachedResponse.headers.get("ETag");
          const lastModified = cachedResponse.headers.get("Last-Modified");

          if (eTag) headers["If-None-Match"] = eTag;
          if (lastModified) headers["If-Modified-Since"] = lastModified;
        }

        // Split the path for API request
        const [basePath, queryString] = fullPath.split("?");
        const apiPath = `/api/route${basePath}${queryString ? `?${queryString}` : ""}`;

        const response = await fetch(apiPath, {
          headers,
        });

        // If 403, refresh because likely preview token as expired
        // if (response.status === 403) {
        //   location.reload();
        // }

        // If the response is 304, the cached content is still valid
        if (response.status === 304 && cachedResponse) {
          pathsBeingFetched.delete(fullPath);
          return; // Skip replacing cache or rendering
        }

        // If the response is OK, update the cache and render
        if (response.ok) {
          const clonedResponse = response.clone();
          await cache.put(fullPath, clonedResponse);
          const content = await response.text();

          if (!isPrefetch || currentRoute === fullPath) {
            renderContent(content);
            updateMetadata(fullPath);
            logNavigation(path); // Log only successful loads (not 404s)
          }
        } else {
          throw new Error(`Failed to fetch content for ${fullPath}`);
        }
      } catch (error) {
        console.error(`Error fetching content for ${fullPath}:`, error);
      } finally {
        pathsBeingFetched.delete(fullPath); // Remove path from active fetch paths
      }
    }

    async function updateContent(fullPath) {
      const cache = await caches.open("route-cache");
      const cachedResponse = await cache.match(fullPath);

      if (cachedResponse) {
        const isStale = true;
        const cachedContent = await cachedResponse.text();

        // Serve cached content immediately
        renderContent(cachedContent);
        updateMetadata(fullPath);

        // If the cache is stale, fetch new content in the background
        if (isStale) {
          fetchContent(fullPath, true);
        }
      } else {
        // No cached content, fetch directly from the network
        startLoading();
        await fetchContent(fullPath);
        completeLoading();
      }
    }

    function renderContent(content) {
      console.log(
        "[Mossy Router] renderContent called, isFirstLoad:",
        isFirstLoad,
      );

      const contentContainer = document.querySelector(
        '[data-mossy-view="content"]',
      );

      // First, extract any script tags with data- attributes from the new content
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = content;

      console.log("[Mossy Router] Content length:", content.length);

      // Count ALL scripts in the incoming content
      const allIncomingScripts = tempDiv.querySelectorAll("script");
      console.log(
        "[Mossy Router] Total scripts in incoming content:",
        allIncomingScripts.length,
      );
      allIncomingScripts.forEach((script, i) => {
        console.log(`  Incoming script ${i}:`, {
          id: script.id,
          type: script.type,
          src: script.src,
          inline: script.textContent.substring(0, 50),
        });
      });

      // Find all data scripts in the new content
      const dataScripts = tempDiv.querySelectorAll('script[id^="mossy-data-"]');

      // Update or add these data scripts to the document head
      dataScripts.forEach((script) => {
        const scriptId = script.getAttribute("id");
        const existingScript = document.getElementById(scriptId);

        if (existingScript) {
          // Replace existing script with new one
          existingScript.textContent = script.textContent;
        } else {
          // Clone and add the script to document head
          const newScript = document.createElement("script");
          newScript.id = scriptId;
          newScript.type = script.type || "application/json";
          newScript.textContent = script.textContent;
          document.head.appendChild(newScript);
        }

        // Remove the script from the content to avoid duplication
        script.parentNode.removeChild(script);
      });

      // Now update the main content
      contentContainer.innerHTML = tempDiv.innerHTML;

      // Only execute scripts if this is not the first load (client-side navigation)
      console.log("[Mossy Router] Should execute scripts?", !isFirstLoad);
      if (!isFirstLoad) {
        document
          .querySelectorAll('script:not([type="application/json"]):not([src])')
          .forEach((oldScript) => {
            const newScript = document.createElement("script");
            Array.from(oldScript.attributes).forEach((attr) => {
              newScript.setAttribute(attr.name, attr.value);
            });
            newScript.textContent = oldScript.textContent;
            oldScript.parentNode.replaceChild(newScript, oldScript);
          });
      }

      document.dispatchEvent(routerEvent);
    }

    async function navigate(path) {
      // Extract base path and query parameters
      const [basePath, queryString] = path.split("?");
      const fullPath = path; // Keep the full path including query params

      window.history.pushState({}, "", fullPath);
      await router();
      window.scrollTo(0, 0);
      document.dispatchEvent(routerEvent);

      // Temp - Init cart on /cart
      if (basePath === "/cart") {
        initCart();
      }
    }

    // Expose navigate function globally for form success actions
    window.mossyNavigate = navigate;

    function updateMetadata(path) {
      const store = grabClientSideData("store");

      if (path === "/") {
        document.title = `${store.workspace.name}`;
      } else if (path === "/products") {
        document.title = `All Products | ${store.workspace.name}`;
      } else if (path.startsWith("/product")) {
        // Check for Product Data
        const product = grabClientSideData("product");
        if (product) {
          document.title =
            product.metadata?.title ||
            `${product.title} | ${store.workspace.name}`;
        }
      } else if (path.startsWith("/page")) {
        // Check for Product Data
        const product = grabClientSideData("page");
        if (product) {
          document.title =
            product.metadata?.title ||
            `${product.title} | ${store.workspace.name}`;
        }
      }
    }

    function attachEventListeners() {
      document.addEventListener("click", handleClick);
      observeLinks();
    }

    function handleClick(e) {
      const anchor = e.target.closest("a");

      if (anchor) {
        const href = anchor.getAttribute("href");

        // Check if it's an internal link (either relative or same origin)
        const isRelativeLink = href?.startsWith("/");
        const isSameOrigin = href?.startsWith(window.location.origin);

        if (isRelativeLink || isSameOrigin) {
          // Extract the path from full URLs
          const path = isRelativeLink
            ? href
            : href.replace(window.location.origin, "");

          // Ignore Clicks During/After Checkout
          if (
            !window.location.pathname.startsWith("/checkout") &&
            !path.startsWith("/checkout")
          ) {
            e.preventDefault();
            navigate(path);
          }
        }
      }
    }

    function grabClientSideData(tag) {
      const productDataScript = document.getElementById(`mossy-data-${tag}`);
      if (productDataScript) {
        try {
          return JSON.parse(productDataScript.textContent);
        } catch (e) {
          console.error("Failed to parse mossy product data:", e);
          return null;
        }
      }
      return null;
    }

    // Prefetch Client-Side Links
    async function observeLinks() {
      if (observer) observer.disconnect();

      observer = new IntersectionObserver(
        async (entries) => {
          const visibleLinks = entries
            .filter((entry) => entry.isIntersecting)
            .map((entry) => entry.target.getAttribute("href"))
            .filter(Boolean);

          const cache = await caches.open("route-cache");

          for (const link of visibleLinks) {
            const cachedResponse = await cache.match(link);

            if (!cachedResponse || isCacheExpired(cachedResponse.headers)) {
              fetchContent(link, true);
            }
          }
        },
        { rootMargin: "200px" },
      );

      document
        .querySelector('[data-mossy-view="content"]')
        .querySelectorAll('a[href^="/"]')
        .forEach((anchor) => {
          observer.observe(anchor);
        });
    }

    // Reattach on Back/Forward Buttons
    window.addEventListener("popstate", () => router());

    // When DOM Loaded, Attach Link/Form Listeners
    document.addEventListener("DOMContentLoaded", () => {
      attachEventListeners();
    });

    // Prefetch Server-Side Links
    document.querySelectorAll('header a[href^="/"]').forEach(async (anchor) => {
      const link = anchor.getAttribute("href");
      const cache = await caches.open("route-cache");
      const cachedResponse = await cache.match(link);

      if (!cachedResponse || isCacheExpired(cachedResponse.headers)) {
        fetchContent(link, true);
      }
    });

    // Initialize router for the first page load
    const path = window.location.pathname || "/";
    const queryString = window.location.search || "";
    const fullPath = path + queryString;
    currentRoute = fullPath;
    latestPath = fullPath;
    updateMetadata(fullPath);

    // Attach event listeners for subsequent navigations
    attachEventListeners();

    // Initialize form handling
    initForms(addToCart);

    // Mark that first load is complete
    isFirstLoad = false;

    resolve();
  });
}
